import { predicate } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { GetStaticPropsResult, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import { RichText } from 'prismic-reactjs';
import { ApplicationForm } from '../../components/form-components/application-form/application-form';
import { Layout } from '../../components';
import { i18nConfig } from '../../next-i18next.config';
import { Client } from '../../prismic-configuration';
import { AssignmentSlugProps, Locale, Params } from '../../types';
import useUpdatePreviewRef from '../../utils/hooks/useUpdatePreviewRef';
import getPrismicMenuByLocale from '../../utils/prismic/menu';
import getPrismicOptionsByLocale from '../../utils/prismic/options';
import { REVALIDATION_TIME } from '../../utils/constants';

const Assignment: NextPage<AssignmentSlugProps> = ({
  options,
  assignment,
  menu,
  preview,
  slug,
}) => {
  const title = RichText.asText(assignment.data.title);
  useUpdatePreviewRef(preview, assignment.id);
  const { tags } = assignment;

  return (
    <>
      <NextSeo
        title={`Prickle | Assignments | ${title}`}
        description={`Prickle | Assignments | ${title}`}
      />
      <Layout options={options} menu={menu}>
        {assignment.data && (
          <>
            <section className="w-full h-full p-6 text-lg text-white bg-dark">
              <div className="container mx-auto">
                {assignment.data.title && (
                  <div className="my-16 text-5xl text-center text-white font-body">
                    {RichText.asText(assignment.data.title)}
                  </div>
                )}
                {assignment.data.description && (
                  <div className="max-w-3xl m-auto mt-16 prose text-white">
                    <PrismicRichText
                      field={assignment.data.description}
                      fallback={<p>No content</p>}
                      components={{
                        heading1: ({ children, key }) => (
                          <h1
                            className="my-5 text-2xl text-white font-body"
                            key={key}
                          >
                            {children}
                          </h1>
                        ),
                        heading2: ({ children, key }) => (
                          <h2
                            className="my-4 text-xl text-white font-body"
                            key={key}
                          >
                            {children}
                          </h2>
                        ),
                        list: ({ children, key }) => (
                          <ul className="mb-5 ml-8 list-disc" key={key}>
                            {children}
                          </ul>
                        ),
                        hyperlink: ({ node, children, key }) => {
                          return (
                            <a
                              href={node.data.url}
                              className="text-primary"
                              key={key}
                            >
                              {children}
                            </a>
                          );
                        },
                      }}
                    />
                  </div>
                )}
              </div>
            </section>
            <section className="max-w-3xl mx-auto my-24 text-black">
              <ApplicationForm
                role={title}
                slug={slug}
                tag={tags[0]}
                endpoint="/api/assignment"
              ></ApplicationForm>
            </section>
          </>
        )}
      </Layout>
    </>
  );
};

export const getStaticPaths = async ({ locales }: { locales: string[] }) => {
  const assignments = await Client().get({
    predicates: [predicate.at('document.type', 'assignment')],
  });
  const paths: { params: { slug: string | null }; locale: string }[] = [];
  assignments.results.forEach((assignment) => {
    locales.forEach((locale) => {
      paths.push({
        params: { slug: assignment.uid },
        locale,
      });
    });
    // assignment.alternate_languages.forEach((locale) => {
    //   paths.push({
    //     params: { slug: assignment.uid },
    //     locale: locale.lang.replace(/-(\w{2})/, (s: string) => s.toUpperCase()),
    //   });
    // });
  });
  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  params,
  preview = false,
  previewData,
  locale,
}: {
  params: Params;
  preview: boolean;
  previewData: any;
  locale: Locale;
  defaultLocale: string;
}): Promise<GetStaticPropsResult<AssignmentSlugProps>> => {
  const menu = await getPrismicMenuByLocale(locale, previewData);
  const options = await getPrismicOptionsByLocale(locale, previewData);
  let assignment;
  try {
    const client = Client({ previewData });
    assignment = await client.getByUID('assignment', params.slug, {
      lang: locale,
      predicates: [predicate.at('my.assignment.active', true)],
    });
  } catch (error: any) {
    if (
      error.message === 'No documents were returned' ||
      error === 'No documents were returned'
    ) {
      return {
        notFound: true,
      };
    }
    throw new Error(error);
  }

  if (!assignment) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'form'], i18nConfig)),
      assignment,
      options,
      menu,
      preview,
      slug: params.slug,
    },
    revalidate: REVALIDATION_TIME,
  };
};

export default Assignment;
